import type {
  Player,
  AdError,
  AdErrorEventData,
  ErrorEventData,
  DrmKeySystem,
  QualityLevel,
} from '@adrise/player';
import {
  PLAYER_EVENTS,
  ErrorType,
} from '@adrise/player';
import { init, reset } from '@adrise/player/lib/action';
import { DialogType } from '@tubitv/analytics/lib/dialog';
import classNames from 'classnames';
import React, { PureComponent, useCallback, useEffect, useRef, useState } from 'react';
import type { WithRouterProps } from 'react-router';
import { withRouter } from 'react-router';

import { isAutoStartFailedError } from 'client/features/playback/error/predictor';
import type { ErrorModalType } from 'client/features/playback/services/ErrorManager';
import { hasPlaybackPageExitManager } from 'client/features/playback/services/PlaybackPageExitManager';
import {
  endVODSession,
  hideErrorModal,
  resetVODPageSession,
  showErrorModal,
} from 'client/features/playback/session/VODPageSession';
import { VODPlaybackSession, VODPlaybackEvents } from 'client/features/playback/session/VODPlaybackSession';
import type { VODPlaybackInfo } from 'client/features/playback/session/VODPlaybackSession';
import {
  trackAdError,
  PlayerType,
  trackPlayerPageExit,
} from 'client/features/playback/track/client-log';
import type { PlayerManagers } from 'client/features/playback/tubiPlayer';
import { isCrawler } from 'client/utils/isCrawler';
import {
  getLocalCaptions,
  loadWebCustomCaptions,
  toggleWebCaptions,
} from 'common/actions/webCaptionSettings';
import {
  START_POS_QUERY,
} from 'common/constants/constants';
import { LOG_SUB_TYPE, TRACK_LOGGING } from 'common/constants/error-types';
import * as eventTypes from 'common/constants/event-types';
import { RESUME_TYPE } from 'common/constants/player';
import { useLocation } from 'common/context/ReactRouterModernContext';
import WebAdAbnormalErrorConstrainView from 'common/experiments/config/webAdAbnormalErrorConstrainView';
import WebIosPlayback, { WEB_IOS_PLAYBACK_VARIANTS } from 'common/experiments/config/webIosPlayback';
import WebLastVideoResourceRetry from 'common/experiments/config/webLastVideoResourceRetry';
import WebRepositionVideoResource from 'common/experiments/config/webRepositionVideoResource';
import { isLoggedInSelector } from 'common/features/authentication/selectors/auth';
import { isMatureContentGatedSelector } from 'common/features/authentication/selectors/needsLogin';
import type VideoResourceManager from 'common/features/playback/services/VideoResourceManager';
import logger from 'common/helpers/logging';
import type { PlaybackComponentExperimentProps } from 'common/HOCs/withExperiment';
import useAppDispatch from 'common/hooks/useAppDispatch';
import useAppSelector from 'common/hooks/useAppSelector';
import useExperiment from 'common/hooks/useExperiment';
import { deviceIdSelector } from 'common/selectors/deviceId';
import { isEspanolModeEnabledSelector, isMobileDeviceSelector } from 'common/selectors/ui';
import FeatureSwitchManager, { AD_MOCK_LIST } from 'common/services/FeatureSwitchManager';
import type { WebCaptionSettingsState } from 'common/types/captionSettings';
import type { History } from 'common/types/history';
import type { TubiThunkDispatch } from 'common/types/reduxThunk';
import type { VideoResource, Video } from 'common/types/video';
import { buildDialogEvent } from 'common/utils/analytics';
import { computeWebCaptionStyles } from 'common/utils/captionTools';
import { isDeepLinkOnWeb } from 'common/utils/deeplinkType';
import { getCurrentPathname } from 'common/utils/getCurrentPathname';
import { trackAppboyEvent, trackEvent, trackLogging } from 'common/utils/track';
import PlaybackInfo from 'ott/features/playback/components/PlaybackInfo/PlaybackInfo';
import { useAds } from 'web/features/playback/components/VideoDetail/hooks/useAds';
import { useAttachAnalyticsEvents } from 'web/features/playback/components/VideoDetail/hooks/useAttachAnalyticsEvents';
import { useAttachGlobals } from 'web/features/playback/components/VideoDetail/hooks/useAttachGlobals';
import { useManageAutostart } from 'web/features/playback/components/VideoDetail/hooks/useManageAutostart';
import { usePlayerFullscreen } from 'web/features/playback/components/VideoDetail/hooks/usePlayerFullscreen';
import { useSetupAutoPlay } from 'web/features/playback/components/VideoDetail/hooks/useSetupAutoPlay';
import { useSetupVodSessions } from 'web/features/playback/components/VideoDetail/hooks/useSetupVodSessions';
import { useTrackVisualQualityChange } from 'web/features/playback/components/VideoDetail/hooks/useTrackVisualQualityChange';
import { useTryFallbackVideoResource } from 'web/features/playback/components/VideoDetail/hooks/useTryFallbackVideoResource';
import { useVideoResourceManager } from 'web/features/playback/components/VideoDetail/hooks/useVideoResourceManager';
import { useWatchHistory } from 'web/features/playback/components/VideoDetail/hooks/useWatchHistory';
import { useTheaterMode } from 'web/features/playback/components/VideoDetail/PlayerArea/hooks/useTheaterMode';
import WebErrorModal from 'web/features/playback/components/WebErrorModal/WebErrorModal';
import { drmKeySystemSelector, isDrmKeySystemReadySelector, isDRMSupportedSelector } from 'web/features/playback/selectors/drm';
import { qualityLevelSelector } from 'web/features/playback/selectors/player';
import { renderControlsSelector } from 'web/features/playback/selectors/ui';
import { getIsFromAutoplay } from 'web/features/playback/utils/getIsFromAutoplay';
import BackgroundImage from 'web/rd/components/BackgroundImage/BackgroundImage';
import ContentDetail from 'web/rd/components/ContentDetail/ContentDetail';

import PlayerArea from './PlayerArea/PlayerArea';
import MoreDetailBottom from './TheaterMode/MoreDetailBottom';
import { getVideoResource, getVideoResourceManager } from './utils/getVideoResource';
import styles from './VideoDetail.scss';

interface ExperimentProps {
  webAdAbnormalErrorConstrainView: ReturnType<typeof WebAdAbnormalErrorConstrainView>;
  webLastVideoResourceRetry: ReturnType<typeof WebLastVideoResourceRetry>;
  webIosPlayback: ReturnType<typeof WebIosPlayback>;
  webRepositionVideoResource: ReturnType<typeof WebRepositionVideoResource>;
}

export interface VideoDetailProps extends PlaybackComponentExperimentProps, WithRouterProps {
  video: Video;
  startPosition: number;
  belongSeries: string | undefined;
  viewHistory?: History;
  series?: Video;
  adBreaks: number[];
  aboveParental?: boolean;
  posterUrl?: string;
  userBirthday?: string;
  seasonIndex?: number;
  episodeIndex?: number;
  isInMobileWhitelist?: boolean;
  // chromecast session connected
  isCasting: boolean;
  captionSettings: WebCaptionSettingsState;
  isContentReady: boolean;
  isFullscreen: boolean;
  isContentLoginGated?: boolean;
  isKidsModeEnabled: boolean;
  isContentUnavailable: boolean;
  performanceCollectorEnabled: boolean;
  contentId: string;
  setNoVideoResourceFound: () => void;
  showRemindMe: boolean;
}

interface PropsFromFunctionalWrapper {
  deviceId?: string;
  isLoggedIn: boolean;
  isEspanolModeEnabled: boolean;
  isDrmKeySystemReady: boolean;
  renderControls: boolean;
  isDeeplink: boolean;
  drmKeySystem?: DrmKeySystem;
  showDRMUnsupportedWarning: boolean;
  videoResourceManager?: VideoResourceManager;
  isDRMSupported: boolean;
  isFromAutoplay: boolean;
  playerRef: React.MutableRefObject<InstanceType<typeof Player> | null>;
  requestFullscreen: (fullscreen: boolean) => void;
  isMobile?: boolean;
  showAutoPlay: boolean;
  playerReady: boolean;
  setPlayerReady: (playerReady: boolean) => void;
  playerError: ErrorEventData | null;
  setPlayerError: (playerError: ErrorEventData | null) => void;
  blockAutoStart: () => void;
  removeStartPositionQueryParam: () => void;
  reportPlaybackSessionData: (retrievedPlaybackInfo?: VODPlaybackInfo) => void;
  isMatureContentGated: boolean;
  isErrorModalOpen: boolean;
  setIsErrorModalOpen: (isOpen: boolean) => void;
  errorModalOpenDetails: string | undefined;
  setErrorModalOpenDetails: (details: string | undefined) => void;
  lastResourceRetryCount: number;
  setLastResourceRetryCount: (count: number) => void;
  qualityLevel: QualityLevel;
  isPrerollEnabled: () => boolean;
  isTheater: boolean;
  positionPlayerRef: React.RefObject<HTMLDivElement>;
  showMetaInTheaterMode: boolean;
  clickMoreDetail: ()=> void;
  attachHistoryHandler: (player: InstanceType<typeof Player>) => void;
  syncHistoryWithServer: (player: InstanceType<typeof Player>) => void;
  trackNotUsedAds: () => void;
  getContentAdUrl: (position: number, resumeType?: string) => string;
  attachAdRules: () => void;
  detachAdRules: () => void;
  attachAutoPlay: () => void;
  detachAutoPlay: () => void;
  attachGlobals: () => void;
  videoResource: VideoResource | undefined;
  setVideoResource: (videoResource: VideoResource | undefined) => void;
  dispatch: TubiThunkDispatch;
  attachAnalyticsEvents: () => void;
  detachAnalyticsEvents: () => void;
  resumePosition: number;
  setResumePosition: (position: number) => void;
  tryFallbackVideoResource: (error: ErrorEventData) => VideoResource | undefined;
  onVisualQualityChange: (event: { qualityIndex: number, level: QualityLevel }) => void;
  getAutoStart: () => boolean,
}

export type VideoDetailClassComponentProps = VideoDetailProps & PropsFromFunctionalWrapper & ExperimentProps;

const NO_PLAYER_INSTANCE = 'No Player instance';

export type ShowAlertModal = (type: ErrorModalType, options: { error: ErrorEventData, errorCode: string }) => void;

// export for test only
export class VideoDetailClassComponent extends PureComponent<VideoDetailClassComponentProps> {

  componentWillUnmount() {
    const {
      video: { id: content_id },
      webRepositionVideoResource,
      video,
      drmKeySystem,
      isDRMSupported,
      videoResourceManager,
      isContentReady,
      videoResource: videoResourceFromProps,
    } = this.props;

    const videoResource = getVideoResource({
      webRepositionVideoResource,
      video,
      drmKeySystem,
      isDRMSupported,
      videoResourceManager,
      isContentReady,
      videoResource: videoResourceFromProps,
    });
    // Must check this before end VOD session to ensure we send the player exit track
    const needToTrackPlayerExitInContainer = !hasPlaybackPageExitManager();
    VODPlaybackSession.getInstance().endPlayback();
    endVODSession();
    VODPlaybackSession.getInstance().getEventEmitter().off(VODPlaybackEvents.reportPlaybackSessionData, this.props.reportPlaybackSessionData);
    /* istanbul ignore next */
    if (needToTrackPlayerExitInContainer && videoResource) {
      trackPlayerPageExit({
        contentId: content_id,
        videoResource,
        player: this.props.playerRef.current ?? undefined,
      });
    }
    // only cleanup document listeners when unmount
    this.props.trackNotUsedAds();
    this.unmountPlayer();
    VODPlaybackSession.getInstance().resetPlaybackInfo();
    resetVODPageSession();
  }

  /**
   * player might be created multiple times after DRM integration,
   * clean up necessary events or loops before create a new player
   */
  unmountPlayer() {
    this.props.dispatch(reset());
    // just a type guard
    /* istanbul ignore next */
    if (!this.props.playerRef.current) return;
    this.props.syncHistoryWithServer(this.props.playerRef.current);
    this.props.detachAutoPlay();
    this.props.detachAdRules();
    this.props.detachAnalyticsEvents();
    this.removePlayer();
  }

  removePlayer = () => {
    if (this.props.playerRef.current && this.props.playerRef.current.remove) {
      this.props.playerRef.current.remove();
      this.props.playerRef.current = null;
    }
  };

  /**
   * called right after the player is initialized
   * register event listeners
   */
  onPlayerCreate = (player: InstanceType<typeof Player>, playerManagers?: PlayerManagers) => {
    const {
      video: { id, duration },
      startPosition, dispatch, isLoggedIn,
      playerRef,
    } = this.props;

    playerRef.current = player;
    dispatch(init(playerRef.current, startPosition, duration));

    this.props.attachHistoryHandler(player);
    this.props.attachAnalyticsEvents();

    const errorManager = playerManagers?.errorManager;
    if (errorManager) {
      errorManager.setShowModal(this.showAlertModal);
    }

    playerRef.current.once(PLAYER_EVENTS.setup, () => this.props.setPlayerReady(true));

    // set the current selected caption as default caption
    // TODO @chun remove this once we get default caption from UAPI
    playerRef.current.on(PLAYER_EVENTS.captionsChange, ({ captionsIndex }: {captionsIndex: number}) => {
      // type guard
      /* istanbul ignore next */
      if (!playerRef.current) throw new Error(NO_PLAYER_INSTANCE);
      const captionsList = playerRef.current.getCaptionsList();
      const { lang, label } = captionsList[captionsIndex];

      const language = lang || label;
      dispatch(toggleWebCaptions({
        ...(language.toLowerCase() !== 'off' ? { language } : {}),
        enabled: language.toLowerCase() !== 'off',
      }));
    });
    this.props.attachAutoPlay();
    this.props.attachAdRules();
    playerRef.current.on(PLAYER_EVENTS.visualQualityChange, this.props.onVisualQualityChange);
    playerRef.current.on(PLAYER_EVENTS.adError, (error: AdError, { ad, adPosition, adsCount, adSequence, isPreroll, lagTime }: AdErrorEventData) => {
      if (!playerRef.current) throw new Error(NO_PLAYER_INSTANCE);
      const adInfo = {
        contentId: id,
        position: playerRef.current.getPosition(),
        url: ad?.video,
        id: ad?.id,
        duration: ad?.duration,
        index: adSequence,
        count: adsCount,
        adPosition,
        isPreroll,
        player: PlayerType.VOD,
        lagTime,
      };
      trackAdError(error, adInfo);

      logger.error({ err: error, contentId: id }, 'Web player ad error');

      if (error && error.name && (error.name === 'NotAllowedError')) {
        this.props.blockAutoStart();
      }
    });
    // track appboy event
    playerRef.current.on(PLAYER_EVENTS.play, () => {
      trackAppboyEvent(eventTypes.APPBOY_START_VIDEO);
    });
    const captionSettingsFromStorage = getLocalCaptions();
    if (captionSettingsFromStorage) {
      this.props.dispatch(loadWebCustomCaptions(captionSettingsFromStorage));
      playerRef.current.setCaptionsStyles(computeWebCaptionStyles(JSON.parse(captionSettingsFromStorage)));
    }
    if (!isLoggedIn) this.props.removeStartPositionQueryParam();

    this.props.attachGlobals();
  };

  playerErrorHandle = (error: ErrorEventData) => {
    if (isAutoStartFailedError(error)) {
      this.props.blockAutoStart();
      return;
    }

    if (error.fatal) {
      if ([ErrorType.SETUP_ERROR, ErrorType.NETWORK_ERROR].includes(error.type as ErrorType)) {
        this.props.setPlayerError(error);
      }
    }
  };

  showAlertModal: ShowAlertModal = (type, options) => {
    const { error, errorCode } = options;
    showErrorModal();
    trackEvent(eventTypes.DIALOG, buildDialogEvent(getCurrentPathname(), DialogType.PLAYER_ERROR, errorCode));
    this.props.setIsErrorModalOpen(true);
    this.props.setErrorModalOpenDetails(error.details);
  };

  handleErrorModalClose = (isRetry: boolean) => {
    const { setErrorModalOpenDetails, setIsErrorModalOpen, video, resumePosition, lastResourceRetryCount, setLastResourceRetryCount } =
      this.props;

    hideErrorModal();

    /**
     * if user clicks "retry" button, increase retry count and reuse current video resource,
     * otherwise set retry count to 0.
     */
    if (isRetry) {
      const nextRetryCount = lastResourceRetryCount + 1;
      trackLogging({
        type: TRACK_LOGGING.videoInfo,
        subtype: LOG_SUB_TYPE.PLAYBACK.VIDEO_RETRY,
        message: { content_id: video.id, retry: nextRetryCount },
      });

      setLastResourceRetryCount(nextRetryCount);
      VODPlaybackSession.getInstance().startPlayback({
        isSeries: !!video.series_id,
        contentId: video.id,
        isAutoplay: this.props.isFromAutoplay,
        isContinueWatching: (resumePosition ?? 0) > 0,
      });
    }
    setErrorModalOpenDetails(undefined);
    setIsErrorModalOpen(false);
  };

  render() {
    const {
      contentId,
      deviceId,
      belongSeries,
      video,
      startPosition,
      series,
      viewHistory,
      posterUrl,
      seasonIndex,
      episodeIndex,
      isMobile,
      adBreaks,
      aboveParental,
      isInMobileWhitelist,
      isCasting,
      userBirthday,
      captionSettings,
      isContentReady,
      isFullscreen,
      isFromAutoplay,
      isContentLoginGated,
      isKidsModeEnabled,
      performanceCollectorEnabled,
      renderControls,
      isContentUnavailable,
      youboraExperimentMap,
      showDRMUnsupportedWarning,
      webRepositionVideoResource,
      isDrmKeySystemReady,
      requestFullscreen,
      showAutoPlay,
      playerReady,
      playerError,
      isMatureContentGated,
      isTheater,
      positionPlayerRef,
      showMetaInTheaterMode,
      clickMoreDetail,
      setNoVideoResourceFound,
      isErrorModalOpen,
      errorModalOpenDetails,
      lastResourceRetryCount,
      drmKeySystem,
      isDRMSupported,
      videoResourceManager: videoResourceManagerFromProps,
      videoResource: videoResourceFromProps,
      showRemindMe,
      resumePosition,
    } = this.props;

    const enableReposition = FeatureSwitchManager.isEnabled(['Player', 'EnableReposition']) || webRepositionVideoResource.getValue();
    const videoResource = getVideoResource(
      {
        webRepositionVideoResource,
        video,
        drmKeySystem,
        isDRMSupported,
        videoResourceManager: videoResourceManagerFromProps,
        isContentReady,
        videoResource: videoResourceFromProps,
      },);
    const videoResourceManager = getVideoResourceManager({
      video,
      drmKeySystem,
      isDRMSupported,
      videoResourceManager: videoResourceManagerFromProps,
      webRepositionVideoResource,
      isContentReady,
    });

    const resumeType = startPosition > 0 ? RESUME_TYPE.STOP : undefined;
    const prerollUrl = this.props.isPrerollEnabled() ? this.props.getContentAdUrl(startPosition, resumeType) : AD_MOCK_LIST.EmptyAd;

    const isEpisode = !!belongSeries;
    let seriesTitle: string | undefined;
    if (series) {
      seriesTitle = series.title;
    }

    const { hero_images: heroImages = [], posterarts: posters = [], backgrounds = [], images = {} } = (isEpisode ? series : video) || {};
    const desktopBackground = backgrounds[0] || heroImages[0] || posters[0];
    const mobileBackground = images.hero_422 && images.hero_422[0];
    const shouldDisableBackground = !isMobile && !isContentUnavailable;

    const position = playerReady && resumePosition ? resumePosition : startPosition;

    const autoStart = this.props.getAutoStart();

    const shouldEnableWebIosPlayback = this.props.webIosPlayback.getValue() !== WEB_IOS_PLAYBACK_VARIANTS.DISABLED;

    const shouldHidePlayer = isMatureContentGated || isContentUnavailable || !isContentReady;

    return (
      <div className={classNames(styles.contentContainer, {
        [styles.transportShowup]: renderControls,
        [styles.fullscreen]: isFullscreen,
        [styles.theater]: isTheater,
      })}
      >
        {shouldDisableBackground ? null : (
          <BackgroundImage preload src={desktopBackground} srcMobile={mobileBackground} />
        )}
        <div
          className={classNames(styles.positionPlayer, {
            [styles.collapsed]: !playerReady,
            [styles.fullscreen]: isFullscreen,
            [styles.hidden]: shouldHidePlayer,
            [styles.theater]: isTheater,
          })}
          ref={positionPlayerRef}
        >
          {shouldHidePlayer ? null : (
            <PlayerArea
              key={`${video.id}-${autoStart ? 'true' : 'false'}`}
              isMobile={!!isMobile}
              belongSeries={belongSeries}
              viewHistory={viewHistory}
              deviceId={deviceId}
              video={video}
              adBreaks={adBreaks}
              aboveParental={aboveParental}
              isContentLoginGated={isContentLoginGated}
              showAutoPlay={showAutoPlay}
              onPlayerCreate={this.onPlayerCreate}
              removePlayer={this.removePlayer}
              prerollUrl={prerollUrl as string}
              mobilePlaybackEnabled={isInMobileWhitelist}
              playerReady={playerReady}
              isCasting={isCasting}
              seriesTitle={seriesTitle}
              userBirthday={userBirthday}
              isContentReady={isContentReady}
              showDRMUnsupportedWarning={showDRMUnsupportedWarning}
              autoStart={autoStart}
              startPosition={position}
              captionSettings={captionSettings}
              playerError={playerError ?? undefined}
              isFullscreen={isFullscreen}
              isFromAutoplay={isFromAutoplay}
              isKidsModeEnabled={isKidsModeEnabled}
              videoResource={enableReposition ? undefined : videoResource}
              getVideoResource={enableReposition ? () => getVideoResource({
                webRepositionVideoResource,
                video,
                drmKeySystem,
                isDRMSupported,
                videoResourceManager,
                isContentReady,
                videoResource,
              }) : undefined}
              getVideoResourceManager={() => getVideoResourceManager({
                video,
                drmKeySystem,
                isDRMSupported,
                videoResourceManager,
                webRepositionVideoResource,
                isContentReady,
              })}
              lastResourceRetryCount={lastResourceRetryCount}
              requestFullscreen={requestFullscreen}
              performanceCollectorEnabled={performanceCollectorEnabled}
              youboraExperimentMap={youboraExperimentMap}
              shouldEnableWebIosPlayback={shouldEnableWebIosPlayback}
              tryFallbackVideoResource={this.props.tryFallbackVideoResource}
              playerErrorHandle={this.playerErrorHandle}
              contentId={contentId}
              setNoVideoResourceFound={setNoVideoResourceFound}
            />
          )}
          {isDrmKeySystemReady && FeatureSwitchManager.isEnabled(['Player', 'Info']) ? /* istanbul ignore next */ <PlaybackInfo
            className={classNames(styles.PlaybackInfoWrapper)}
            videoResourceManager={videoResourceManager ?? undefined}
            youboraExperimentMap={youboraExperimentMap}
          /> : null }
        </div>
        { !isTheater ? null : (
          <div className={classNames(styles.moreDetailBottom, {
            [styles.visible]: !showMetaInTheaterMode,
            [styles.hidden]: showMetaInTheaterMode,
          })}
          >
            <MoreDetailBottom onClick={clickMoreDetail} />
          </div>)
        }
        <div className={classNames(styles.positionContentDetail, {
          [styles.hidden]: isFullscreen,
          [styles.noPlayer]: shouldHidePlayer,
          [styles.theater]: isTheater,
        })}
        >
          {isContentReady && <ContentDetail
            belongSeries={belongSeries}
            content={video}
            seasons={(series || {}).seasons}
            seriesTitle={isEpisode ? seriesTitle : undefined}
            posterUrl={posterUrl}
            seasonIndex={seasonIndex}
            episodeIndex={episodeIndex}
            shouldShowContentUnavailable={isContentUnavailable}
            showRemindMe={showRemindMe}
            audioTracks={videoResource?.audio_tracks}
            isMatureContentGated={isMatureContentGated}
          />}
        </div>
        <WebErrorModal
          isOpen={isErrorModalOpen}
          onClose={this.handleErrorModalClose}
          playerErrorDetails={errorModalOpenDetails}
        />
      </div>
    );
  }
}

const VideoDetail = (props: VideoDetailProps) => {
  const {
    contentId,
    isContentReady,
    video,
    location: { query },
    adBreaks,
    belongSeries,
    startPosition,
  } = props;

  const dispatch = useAppDispatch();

  const { duration } = video;

  // ✨🌟✨ the player ✨🌟✨
  const playerRef = useRef<InstanceType<typeof Player> | null>(null);

  // route derivations
  const isFromAutoplay = getIsFromAutoplay(query);

  // Redux store derivations
  const deviceId = useAppSelector(deviceIdSelector);
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const isEspanolModeEnabled = useAppSelector(isEspanolModeEnabledSelector);
  const isDrmKeySystemReady = useAppSelector(isDrmKeySystemReadySelector);
  const renderControls = useAppSelector(renderControlsSelector);
  const drmKeySystem = useAppSelector(drmKeySystemSelector);
  const isDRMSupported = useAppSelector(isDRMSupportedSelector);
  const isMobile = useAppSelector(isMobileDeviceSelector);
  const isMatureContentGated = useAppSelector((state) => isMatureContentGatedSelector(state, contentId));
  const qualityLevel = useAppSelector(qualityLevelSelector);
  const location = useLocation();
  const [resumePosition, setResumePosition] = useState(0);

  // Prop derivations
  const isDeeplink = isDeepLinkOnWeb(query);

  // VOD page and VOD playback session initialization
  const { reportPlaybackSessionData } = useSetupVodSessions({ video, isFromAutoplay, isDeeplink, resumePosition });

  // Experiments
  const webAdAbnormalErrorConstrainView = useExperiment(WebAdAbnormalErrorConstrainView);
  const webLastVideoResourceRetry = useExperiment(WebLastVideoResourceRetry);
  useEffect(() => {
    webLastVideoResourceRetry.logExposure();
  }, [webLastVideoResourceRetry]);
  const webIosPlayback = useExperiment(WebIosPlayback);
  const webRepositionVideoResource = useExperiment(WebRepositionVideoResource);

  // Video resource (old location pending reposition experiment)
  const {
    videoResourceManager,
    showDRMUnsupportedWarning,
    videoResource,
    setVideoResource,
  } = useVideoResourceManager({ video, isContentReady });
  const enableReposition = FeatureSwitchManager.isEnabled(['Player', 'EnableReposition']) || webRepositionVideoResource.getValue();

  // Fullscreen
  const { requestFullscreen } = usePlayerFullscreen({ contentId, playerRef });

  // History
  const { attachHistoryHandler, syncHistoryWithServer } = useWatchHistory({
    contentId,
    belongSeries,
    isLoggedIn,
    duration,
    location,
  });

  // State atoms

  // TODO: playerReady state and the useEffect below can probably
  // migrate into a hook that handles player setup once this exists
  const [playerReady, setPlayerReady] = useState(false);
  useEffect(() => {
  // Render the video element immediately for the crawler
    if (isCrawler()) {
      setPlayerReady(true);
    }
  }, []);

  const [playerError, setPlayerError] = useState<ErrorEventData | null>(null);

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalOpenDetails, setErrorModalOpenDetails] = useState<string | undefined>();
  const [lastResourceRetryCount, setLastResourceRetryCount] = useState(0);

  const removeStartPositionQueryParam = useCallback(() => {
    // if we have a stored position as result of custom captions update, we need to clear after player starts
    const startedFromStoredPosition = window.location.search.indexOf(START_POS_QUERY) > -1;
    if (startedFromStoredPosition) {
      window.history.replaceState(null, '', window.location.href.replace(/\?startPos=\d*/, ''));
    }
  }, []);

  const { showAutoPlay, attachAutoPlay, detachAutoPlay } = useSetupAutoPlay({ playerRef, video });

  const { tryFallbackVideoResource } = useTryFallbackVideoResource({
    video,
    videoResourceManager,
    setResumePosition,
    startPosition,
    playerRef,
    setVideoResource,
  });

  const {
    isPrerollEnabled,
    trackNotUsedAds,
    getContentAdUrl,
    attachAdRules,
    detachAdRules,
  } = useAds({
    adBreaks,
    playerRef,
    isFromAutoplay,
    contentId,
    publisherId: video.publisher_id,
    isDeeplink,
    showAutoPlay,
  });

  const { isTheater, showMetaInTheaterMode, clickMoreDetail, positionPlayerRef } = useTheaterMode({
    dispatch,
  });

  const { attachGlobals } = useAttachGlobals({ playerRef, video });

  const { attachAnalyticsEvents, detachAnalyticsEvents } = useAttachAnalyticsEvents({
    playerRef,
    qualityLevel,
    startPosition,
    video,
    webRepositionVideoResource,
    drmKeySystem,
    isDRMSupported,
    videoResourceManager,
    isContentReady,
    videoResource,
  });

  const { onVisualQualityChange } = useTrackVisualQualityChange({
    video,
    playerRef,
    videoResourceManager,
    isContentReady,
    videoResource,
  });

  const { getAutoStart, blockAutoStart } = useManageAutostart({ playerReady });

  return (
    <VideoDetailClassComponent
      {...props}
      dispatch={dispatch}
      deviceId={deviceId}
      isLoggedIn={isLoggedIn}
      isEspanolModeEnabled={isEspanolModeEnabled}
      isDrmKeySystemReady={isDrmKeySystemReady}
      renderControls={renderControls}
      isDeeplink={isDeeplink}
      webAdAbnormalErrorConstrainView={webAdAbnormalErrorConstrainView}
      webLastVideoResourceRetry={webLastVideoResourceRetry}
      webIosPlayback={webIosPlayback}
      webRepositionVideoResource={webRepositionVideoResource}
      showDRMUnsupportedWarning={showDRMUnsupportedWarning}
      videoResourceManager={enableReposition ? undefined : videoResourceManager}
      drmKeySystem={drmKeySystem}
      isDRMSupported={isDRMSupported}
      playerRef={playerRef}
      requestFullscreen={requestFullscreen}
      isMobile={isMobile}
      isFromAutoplay={isFromAutoplay}
      showAutoPlay={showAutoPlay}
      playerReady={playerReady}
      setPlayerReady={setPlayerReady}
      playerError={playerError}
      setPlayerError={setPlayerError}
      blockAutoStart={blockAutoStart}
      removeStartPositionQueryParam={removeStartPositionQueryParam}
      reportPlaybackSessionData={reportPlaybackSessionData}
      isMatureContentGated={isMatureContentGated}
      isErrorModalOpen={isErrorModalOpen}
      setIsErrorModalOpen={setIsErrorModalOpen}
      errorModalOpenDetails={errorModalOpenDetails}
      setErrorModalOpenDetails={setErrorModalOpenDetails}
      lastResourceRetryCount={lastResourceRetryCount}
      setLastResourceRetryCount={setLastResourceRetryCount}
      qualityLevel={qualityLevel}
      isPrerollEnabled={isPrerollEnabled}
      isTheater={isTheater}
      positionPlayerRef={positionPlayerRef}
      showMetaInTheaterMode={showMetaInTheaterMode}
      clickMoreDetail={clickMoreDetail}
      attachHistoryHandler={attachHistoryHandler}
      syncHistoryWithServer={syncHistoryWithServer}
      trackNotUsedAds={trackNotUsedAds}
      getContentAdUrl={getContentAdUrl}
      attachAdRules={attachAdRules}
      detachAdRules={detachAdRules}
      attachAutoPlay={attachAutoPlay}
      detachAutoPlay={detachAutoPlay}
      attachGlobals={attachGlobals}
      videoResource={videoResource}
      setVideoResource={setVideoResource}
      attachAnalyticsEvents={attachAnalyticsEvents}
      detachAnalyticsEvents={detachAnalyticsEvents}
      resumePosition={resumePosition}
      setResumePosition={setResumePosition}
      tryFallbackVideoResource={tryFallbackVideoResource}
      onVisualQualityChange={onVisualQualityChange}
      getAutoStart={getAutoStart}
    />
  );
};

export default withRouter(VideoDetail);
